import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api, upload } from '../../lib/api';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { useToast } from '../../components/Toast';

/*
 * غرفةُ اجتماعٍ داخليّةٌ عبر Jitsi.
 *
 * تُضمَّن داخلَ النظامِ فلا يخرجُ المستخدمُ إلى تبويبٍ أو أداةٍ خارجيّة.
 * صاحبُ الاجتماعِ (الهوست) يملكُ كاملَ الأدواتِ وإدارةَ المشاركين، والضيفُ
 * يتحكّمُ في صوته وصورته كما في زوم أو جوجل ميت. التسجيلُ للهوست: يُلتقط
 * داخلَ المتصفّحِ ثمّ يُرفَع ويُلحَق بالاجتماعِ تلقائيًّا فيظهرُ في قائمته.
 */

interface MeetingActivity {
  id: string;
  subject: string;
  type: string;
  status: string;
  meetingProvider: string | null;
  meetingUrl: string | null;
  ownerId: string | null;
  assigneeId: string | null;
  createdBy: string | null;
}

/** يفكّ نطاقَ Jitsi واسمَ الغرفةِ من الرابطِ المخزَّن. */
function parseJitsi(url: string): { domain: string; room: string } | null {
  try {
    const u = new URL(url);
    const room = u.pathname.replace(/^\/+/, '');
    if (!room) return null;
    return { domain: u.host, room };
  } catch {
    return null;
  }
}

/* وعدُ تحميلٍ واحدٌ على مستوى الوحدة — يتجنّبُ سباقَ التركيبِ المزدوجِ في
 * وضعِ التطويرِ (StrictMode) وتكرارَ حقنِ السكربت. */
let jitsiLoad: Promise<void> | null = null;
function loadJitsiApi(domain: string): Promise<void> {
  const w = window as unknown as { JitsiMeetExternalAPI?: unknown };
  if (w.JitsiMeetExternalAPI) return Promise.resolve();
  if (!jitsiLoad) {
    jitsiLoad = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = `https://${domain}/external_api.js`;
      s.async = true;
      document.head.appendChild(s);
      /* استطلاعٌ للمُعرَّفِ العامِّ بدل الاعتمادِ على حدثِ التحميلِ وحده —
       * أمتنُ أمامَ اختلافِ توقيتِ الأحداثِ عبرَ المتصفّحات. */
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (w.JitsiMeetExternalAPI) {
          window.clearInterval(timer);
          resolve();
        } else if (Date.now() - started > 15000) {
          window.clearInterval(timer);
          jitsiLoad = null;
          reject(new Error('تعذّر تحميل مكوّن الاجتماع.'));
        }
      }, 150);
    });
  }
  return jitsiLoad;
}

const GUEST_TOOLBAR = [
  'microphone',
  'camera',
  'desktop',
  'tileview',
  'raisehand',
  'chat',
  'fullscreen',
  'settings',
  'hangup',
];

export function MeetingRoom() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { session } = useAuth();
  const user = session?.user;

  const meeting = useQuery({
    queryKey: ['activity', id],
    queryFn: () => api<MeetingActivity>(`/crm/activities/${id}`),
    enabled: !!id,
  });

  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apiRef = useRef<any>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);

  const data = meeting.data;
  const isHost =
    !!data &&
    !!user &&
    (data.ownerId === user.id || data.createdBy === user.id || data.assigneeId === user.id);
  const jitsi = data?.meetingUrl ? parseJitsi(data.meetingUrl) : null;

  useEffect(() => {
    if (!data || !jitsi || !containerRef.current) return;
    let disposed = false;

    void loadJitsiApi(jitsi.domain)
      .then(() => {
        if (disposed || !containerRef.current || apiRef.current) return;
        const w = window as unknown as { JitsiMeetExternalAPI: new (d: string, o: unknown) => unknown };
        const externalApi = new w.JitsiMeetExternalAPI(jitsi.domain, {
          roomName: jitsi.room,
          parentNode: containerRef.current,
          userInfo: { displayName: user?.name ?? 'مشارك' },
          configOverwrite: {
            prejoinPageEnabled: false,
            disableDeepLinking: true,
            startWithAudioMuted: !isHost,
            startWithVideoMuted: false,
          },
          interfaceConfigOverwrite: {
            SHOW_JITSI_WATERMARK: false,
            DEFAULT_BACKGROUND: '#0b0f1a',
            /* الضيفُ بأدواتٍ محدودة؛ الهوستُ بكاملِ الأدواتِ الافتراضيّة. */
            ...(isHost ? {} : { TOOLBAR_BUTTONS: GUEST_TOOLBAR }),
          },
        });
        apiRef.current = externalApi;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (externalApi as any).addListener('videoConferenceJoined', () => setReady(true));
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (externalApi as any).addListener('readyToClose', () => navigate('/crm/meetings'));
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : 'تعذّر بدء الاجتماع.'));

    return () => {
      disposed = true;
      try {
        recorderRef.current?.stop();
      } catch {
        /* تجاهل */
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      try {
        (apiRef.current as any)?.dispose?.();
      } catch {
        /* تجاهل */
      }
      apiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.id, jitsi?.domain, jitsi?.room, isHost]);

  /* التسجيل: التقاطُ الشاشةِ/التبويبِ داخلَ المتصفّحِ ثمّ الرفعُ والإلحاق. */
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: { frameRate: 15 },
        audio: true,
      });
      const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : 'video/webm';
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks: Blob[] = [];
      rec.ondataavailable = (e) => {
        if (e.data.size) chunks.push(e.data);
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        setUploading(true);
        try {
          const blob = new Blob(chunks, { type: 'video/webm' });
          const form = new FormData();
          form.append('entity', 'activity');
          form.append('entityId', id);
          form.append('file', blob, `meeting-${Date.now()}.webm`);
          form.append('note', 'تسجيل اجتماع Jitsi');
          const att = await upload<{ id: string }>('/attachments', form);
          await api(`/crm/activities/${id}`, { method: 'PATCH', body: { recordingUrl: att.id } });
          toast('حُفظ التسجيلُ وأُلحق بالاجتماع', 'success');
        } catch (err) {
          toast(err instanceof Error ? err.message : 'تعذّر حفظُ التسجيل');
        } finally {
          setUploading(false);
        }
      };
      /* توقّفُ المستخدمِ لمشاركةِ الشاشةِ يوقفُ التسجيل. */
      stream.getVideoTracks()[0]?.addEventListener('ended', () => {
        if (rec.state !== 'inactive') rec.stop();
      });
      rec.start(1000);
      recorderRef.current = rec;
      setRecording(true);
      toast('بدأ التسجيل — اختر نافذةَ الاجتماعِ للمشاركة', 'success');
    } catch {
      toast('أُلغي التقاطُ الشاشةِ أو تعذّر.');
    }
  };

  const stopRecording = () => {
    try {
      recorderRef.current?.stop();
    } catch {
      /* تجاهل */
    }
  };

  if (meeting.isPending) {
    return <div className="mr-state">جارٍ فتحُ الاجتماع…</div>;
  }
  if (meeting.isError || !data) {
    return (
      <div className="mr-state">
        تعذّر تحميلُ الاجتماع.
        <Link to="/crm/meetings" className="btn btn-sm mt-3">
          عودة للاجتماعات
        </Link>
      </div>
    );
  }
  if (data.meetingProvider !== 'JITSI' || !jitsi) {
    return (
      <div className="mr-state">
        هذا الاجتماعُ ليس اجتماعًا داخليًّا (Jitsi).
        {data.meetingUrl && (
          <a className="btn btn-primary btn-sm mt-3" href={data.meetingUrl} target="_blank" rel="noreferrer">
            فتحُ رابطِ الاجتماع
          </a>
        )}
      </div>
    );
  }

  return (
    <div className="meeting-room">
      <div className="mr-bar">
        <Link to="/crm/meetings" className="btn btn-sm btn-icon" title="عودة" aria-label="عودة">
          <Icon name="arrowRight" size={16} />
        </Link>
        <div className="mr-title">
          <Icon name="camera" size={16} />
          <span>{data.subject}</span>
          <span className={`mr-role ${isHost ? 'is-host' : ''}`}>
            {isHost ? 'مضيف' : 'ضيف'}
          </span>
        </div>
        {isHost && (
          <div className="mr-actions">
            {uploading ? (
              <span className="tiny mute">جارٍ حفظُ التسجيل…</span>
            ) : recording ? (
              <button className="btn btn-sm btn-danger" onClick={stopRecording}>
                <span className="mr-rec-dot" /> إيقاف التسجيل
              </button>
            ) : (
              <button className="btn btn-sm" onClick={() => void startRecording()}>
                <Icon name="video" size={14} /> تسجيل الاجتماع
              </button>
            )}
          </div>
        )}
      </div>

      {loadError ? (
        <div className="mr-state">{loadError}</div>
      ) : (
        <div className="mr-stage">
          {!ready && <div className="mr-loading">جارٍ الاتصال بالاجتماع…</div>}
          <div className="mr-frame" ref={containerRef} />
        </div>
      )}
    </div>
  );
}
